import React, {
  ChangeEventHandler,
  ComponentPropsWithRef,
  ComponentType,
  useMemo,
  // useState,
} from 'react';
import { BaseWithFormProps } from './withFormProps';
import {
  ComponentDescriptor,
  FormInterpreter,
  getRendererFromDescriptor,
  useField,
  WizardInterpreter,
} from '@kizi/comparator-core';

/**
 * @typedef {object} FormFieldProps
 * @property {string} fieldName
 * corresponds to the field name in the form machine of the current step
 */
export interface FormFieldProps {
  fieldName: string;
  component: ComponentDescriptor;
}

export interface FormFieldHOCProps {
  _fieldName: string;
  _service?: FormInterpreter | WizardInterpreter;
  // _showErrorOnBlur?: boolean;
  customSideLabel?: any;
}

export type WithAtLeast<P> = {
  [key: string]: any;
} & P;
export type InputMode =
  | 'text'
  | 'none'
  | 'search'
  | 'tel'
  | 'url'
  | 'email'
  | 'numeric'
  | 'decimal';

export type FormPropsWithValue = {
  name?: string;
  value?: any;
  onChange?: ChangeEventHandler<any>;
  onBlur?: React.EventHandler<any>;
  customSideLabel?: any;
  onKeyDown?: React.KeyboardEventHandler<any>;
  onPaste?: React.ClipboardEventHandler<any>;
  type?: string;
  inputMode?: InputMode;
  maxLength?: number;
} & BaseWithFormProps;

export type BaseHandler<
  HOCProps,
  C extends ComponentType<any> = ComponentType,
> = (Component: C) => ComponentType<HOCProps & ComponentPropsWithRef<C>>;

export const FormFieldHOC: BaseHandler<
  FormFieldHOCProps,
  ComponentType<FormPropsWithValue>
> =
  (FieldComponent) =>
  ({
    _fieldName,
    _service,
    onBlur: onBlurProp,
    onChange: onChangeProp,
    onKeyDown,
    onPaste,
    type,
    inputMode,
    maxLength,
    ...ogProps
  }) => {
    const field = useField(_fieldName, _service);
    const name = field ? field.name : undefined;
    const value = field ? field.value : undefined;
    const label = ogProps._label !== undefined ? ogProps._label : field?.label;
    const onChange = onChangeProp ?? field?.onChange ?? undefined;
    const error = field && field.errors ? field.errors.join('\n') : undefined;
    const active = field?.active !== false;
    const onBlur = onBlurProp ?? field?.onBlur ?? undefined;
    const options = field ? field.options : undefined;

    return useMemo(() => {
      return (
        <>
          {active && (
            <FieldComponent
              {...ogProps}
              name={name}
              value={value}
              _error={error}
              _label={label}
              _options={options}
              onChange={onChange}
              onBlur={onBlur}
              onPaste={onPaste}
              {...(type ? { type: type } : {})}
              {...(onKeyDown ? { onKeyDown: onKeyDown } : {})}
              {...(inputMode ? { inputMode: inputMode } : {})}
              {...(maxLength ? { maxLength: maxLength } : {})}
            />
          )}
        </>
      );
    }, [
      error,
      name,
      value,
      active,
      label,
      ogProps['options'],
      options,
      ogProps['customSideLabel'],
    ]);
  };

export const FormField: React.FC<FormFieldProps> = ({
  fieldName,
  component,
}) => {
  const field = useField(fieldName);
  const name = field ? field.name : undefined;
  const value = field ? field.value : undefined;
  const _error = field && field.errors ? field.errors.join('\n') : undefined;
  const active = field?.active !== false;
  const _label = field ? field.label : undefined;
  const onChange = field ? field.onChange : undefined;
  const onBlur = field ? field.onBlur : undefined;
  const FieldComponent = useMemo(() => {
    return getRendererFromDescriptor({
      ...component,
      props: {
        _label,
        ...component.props,
        name,
        value,
        _error,
        onChange,
        onBlur,
      },
    });
  }, [
    component.type,
    component._children,
    name,
    value,
    _error,
    active,
    _label,
  ]);
  return useMemo(() => {
    return <> {active && FieldComponent} </>;
  }, [_error, name, value, active]);
};
